import { Box, Button, Card, Grid, IconButton, Typography } from '@mui/material';
import ScrollForwardIcon from '@mui/icons-material/ArrowForwardIosRounded';
import ScrollBackIcon from '@mui/icons-material/ArrowBackIosNewRounded';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  handleHorizontalClicked,
  handleHorizontalScroll,
  LoadingIndicator,
  useWindow,
  FunctionBuilder,
} from '@xolv-mybrightlink/common';
import { ArticleCard } from './ArticleCard';
import { FormattedMessage } from 'react-intl';
import { useFetchPreviewArticles } from '../hooks/useFetchArticles';
import { Article } from '../models/ArticleResult';

export const ArticleListPreview = () => {
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [articles, setArticles] = useState<Article[][]>([]);
  const [retry, setRetry] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const articleFetchResult = useFetchPreviewArticles(undefined, null, 7, retry);

  const checkHorizontalScrollable = useCallback(() => {
    if (containerRef.current && articles.length > 0) {
      setCanScrollRight(
        containerRef.current.scrollWidth - containerRef.current.clientWidth > 0,
      );
    }
  }, [containerRef, articles.length]);

  useEffect(() => {
    if (articleFetchResult.type === 'DATA') {
      const groupArticles: Article[][] = [[articleFetchResult.articles[0]]];
      for (let i = 1; i < articleFetchResult.articles.length; i += 2) {
        groupArticles.push(articleFetchResult.articles.slice(i, i + 2));
      }
      setArticles(groupArticles);
    }
  }, [articleFetchResult]);

  useEffect(() => {
    checkHorizontalScrollable();
  }, [checkHorizontalScrollable]);

  useWindow(() => {
    checkHorizontalScrollable();
  });

  const onScroll = () => {
    handleHorizontalScroll(containerRef, (canScrollLeft, canScrollRight) => {
      setCanScrollLeft(canScrollLeft);
      setCanScrollRight(canScrollRight);
    });
  };

  const onScrollIconClicked = (direction: 'backward' | 'forward') => {
    handleHorizontalClicked(
      containerRef,
      direction,
      (canScrollLeft, canScrollRight) => {
        setCanScrollLeft(canScrollLeft);
        setCanScrollRight(canScrollRight);
      },
    );
  };

  return (
    <Box
      height={320}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <FunctionBuilder>
        {() => {
          if (articleFetchResult.type === 'LOADING') {
            return <LoadingIndicator />;
          } else if (articleFetchResult.type === 'ERROR') {
            return (
              <Card
                elevation={8}
                sx={{
                  width: '100%',
                  height: '100%',
                  borderRadius: 5,
                  m: 2,
                  padding: 0,
                  background: 'transparent',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  flexDirection: 'column',
                }}
              >
                <Typography
                  sx={{
                    textAlign: 'center',
                  }}
                >
                  <FormattedMessage
                    id='common.error.fetch.fail'
                    values={{ br: <br /> }}
                  />
                </Typography>
                <Button
                  variant={'contained'}
                  disableElevation
                  onClick={() => setRetry(!retry)}
                  sx={{
                    width: '20%',
                    height: '18%',
                    borderRadius: 8,
                    mt: 10,
                    fontSize: 'inherit',
                  }}
                >
                  <FormattedMessage id='common.try-again' />
                </Button>
              </Card>
            );
          } else {
            return (
              <Box
                sx={{
                  position: 'relative',
                  display: 'flex',
                  width: '100%',
                  height: '100%',
                  alignItems: 'center',
                }}
              >
                {articles.length > 0 && (
                  <Grid
                    ref={containerRef}
                    onScroll={onScroll}
                    className='hide-scrollbar'
                    wrap='nowrap'
                    container
                    sx={{
                      overflow: 'auto',
                      borderRadius: 5,
                      mr: 5,
                      height: '100%',
                      boxShadow:
                        '0 12px 16px 0 rgba(59, 69, 89, 0.05), 0 8px 10px 0 rgba(59, 69, 89, 0.1)',
                    }}
                  >
                    {articles[0][0] && (
                      <Grid
                        item
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          minWidth: 280,
                          pb: 0.25,
                        }}
                      >
                        <ArticleCard article={articles[0][0]} isFirstArticle />
                      </Grid>
                    )}
                    <Grid item xs>
                      <Grid
                        container
                        wrap='nowrap'
                        direction='row'
                        sx={{
                          height: '100%',
                          width: '100%',
                        }}
                      >
                        {articles.slice(1).map((articleGroup, groupIndex) => (
                          <Grid
                            key={groupIndex}
                            container
                            direction='column'
                            sx={{ height: '100%', width: '40%', minWidth: 249 }}
                          >
                            {articleGroup.map((article, index) => (
                              <Grid
                                key={index}
                                item
                                xs
                                sx={{
                                  pl: 0.25,
                                  pb: 0.25,
                                }}
                              >
                                <ArticleCard
                                  key={article.databaseId}
                                  article={article}
                                />
                              </Grid>
                            ))}
                          </Grid>
                        ))}
                      </Grid>
                    </Grid>
                  </Grid>
                )}
                {canScrollLeft && (
                  <IconButton
                    onClick={() => onScrollIconClicked('backward')}
                    sx={{
                      borderRadius: 5,
                      backgroundColor: '#ffffff',
                      position: 'absolute',
                      width: 40,
                      height: 40,
                      left: -12,
                      boxShadow: 4,
                      p: 5,
                      '&:hover': {
                        backgroundColor: 'white',
                      },
                    }}
                  >
                    <ScrollBackIcon
                      sx={{
                        color: '#898f9b',
                      }}
                    />
                  </IconButton>
                )}
                {canScrollRight && (
                  <IconButton
                    onClick={() => onScrollIconClicked('forward')}
                    sx={{
                      borderRadius: 20,
                      backgroundColor: '#ffffff',
                      position: 'absolute',
                      width: 40,
                      height: 40,
                      right: 4,
                      boxShadow: 4,
                      p: 5,
                      '&:hover': {
                        backgroundColor: 'white',
                      },
                    }}
                  >
                    <ScrollForwardIcon
                      sx={{
                        color: '#898f9b',
                      }}
                    />
                  </IconButton>
                )}
              </Box>
            );
          }
        }}
      </FunctionBuilder>
    </Box>
  );
};
